/**
 * หน้าจอเคส NCR (Non-Conformance Report, P2-11b, D-108 ข้อ 2 และ 3) — รายการ
 * เปิดเคส สอบสวน และปิดเคสด้วยการตัดสินใจ
 *
 * ปิดห้าเส้นทางที่ `WO-P2-M11-007` เปิดไว้: `GET`/`POST /v1/ncr-cases`, `GET`
 * หนึ่งใบ (ผ่านรายการ ไม่มีหน้ารายละเอียดแยก) และสองการเปลี่ยนสถานะ
 * `POST .../investigate` กับ `POST .../close`
 *
 * โครงยืมมาจาก `IncomingInspectionRuleScreen` ในโฟลเดอร์เดียวกัน (ช่องกรองบนสุด
 * ตารางรายการ ฟอร์มอยู่หลังปุ่มในกล่อง `Modal` และหลังสำเร็จก็โหลดรายการใหม่ผ่าน
 * client เส้นเดิม) — `desktop/quality/` มีสองหน้าจอแล้วตอนนี้ ทั้งคู่ gate ด้วย
 * `INVENTORY` เดียวกันเพราะทั้งสองเป็นสิทธิ์ชุดเดียวกับที่คนคลัง/คนรับของถืออยู่แล้ว
 * (D-086, D-108)
 *
 * ห้าข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้ (invariant ของ WO นี้):
 *
 * 1. **THE CONSEQUENCE HEADLINE** — เลือก disposition ในกล่องปิดเคสแล้วต้องเห็น
 *    ทันทีว่าจะกระทบล็อตไหนและล็อตนั้นจะไปอยู่สถานะอะไร **ก่อน** กดยืนยัน
 *    (`closeConsequencePreview`, `ncrView.ts`)
 * 2. **สี่ disposition ไม่เท่ากัน** — `SCRAP` ต้องเด่นกว่าตัวเลือกอื่นเพราะมันคือ
 *    แบบเดียวที่ทำลายวัสดุถาวร (`DISPOSITION_DESCRIPTORS` เรียงตามน้ำหนักผล)
 * 3. **เคสที่ปิดแล้วไม่ถูกซ่อนปุ่ม** — ปุ่ม "สอบสวน"/"ปิดเคส" แสดงตามสิทธิ์เท่านั้น
 *    ไม่ตามสถานะของเคส กดแล้วเซิร์ฟเวอร์ตอบ `422 NCR_CASE_IS_CLOSED` จริง ๆ ซึ่ง
 *    หน้าจอเรนเดอร์คำแนะนำนั้นตรง ๆ ("ยกเคสใหม่แทน") — การซ่อนปุ่มจะทำให้ผู้ใช้ไม่
 *    เห็นกติกาข้อนี้เลย
 * 4. **อ้างอิงแสดงตามที่มันเป็นจริง** — ชนิดและ id ดิบ ไม่เดาว่า resolve ไปเป็นอะไร
 *    และไม่สร้างลิงก์ไปหน้าจอที่อาจไม่มีอยู่สำหรับชนิดนั้น (D-034)
 * 5. **ขอบเขตโรงงานเป็นคำตอบของเซิร์ฟเวอร์เสมอ** — ไม่กรองซ้ำฝั่งเบราว์เซอร์
 *
 * การ gate สิทธิ์: `INVENTORY` + `VIEW` เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่
 * ที่ route) ส่วน `CREATE` (เปิดเคสใหม่), `EDIT` (สอบสวน) และ `APPROVE` (ปิดเคส —
 * แรงที่สุดเพราะมันย้ายสถานะล็อตจริง) เป็นสิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูกบังคับ
 * ข้างในนี้ทีละปุ่ม ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  closeNcrCase,
  createNcrCase,
  fetchNcrCases,
  investigateNcrCase,
  ruleFromFailure,
  type NcrCaseFailure,
} from '../../lib/ncr/client';
import {
  DISPOSITION_DESCRIPTORS,
  EMPTY_CREATE_NCR_CASE_FIELDS,
  closeConsequencePreview,
  isNcrDisposition,
  lotStatusLabel,
  ncrReferenceTypeLabel,
  ncrStatusLabel,
  ncrStatusTone,
  toCloseNcrCaseBody,
  toCreateNcrCaseBody,
  toInvestigateNcrCaseBody,
  toNcrPlantCodeFilter,
  toNcrStatusFilter,
  type CreateNcrCaseFields,
} from '../../lib/ncr/ncrView';
import {
  DEFECT_DESCRIPTION_MAX_LENGTH,
  INVENTORY_MODULE,
  INVESTIGATION_NOTE_MAX_LENGTH,
  NCR_CASE_FIELD_IS_SERVER_OWNED,
  NCR_CASE_IS_CLOSED,
  NCR_CASE_NOT_OPEN,
  NCR_REFERENCE_DOC_TYPES,
  NCR_REFERENCE_NOT_FOUND,
  NCR_REFERENCE_PLANT_MISMATCH,
  NCR_STATUSES,
  PLANT_CODE_MAX_LENGTH,
  PLANT_OUTSIDE_SCOPE,
  type NcrCase,
  type NcrDisposition,
} from '../../lib/ncr/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type NcrFailureContext = 'list' | 'create' | 'investigate' | 'close';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function nothingChangedText(context: NcrFailureContext): string {
  switch (context) {
    case 'create':
      return ' ไม่มีเคสใดถูกเปิด และไม่มีแถว audit ถูกเขียน';
    case 'investigate':
      return ' เคสนี้ยังอยู่ที่สถานะเดิม ไม่มีอะไรถูกบันทึกเพิ่ม';
    case 'close':
      return ' เคสนี้ยังไม่ถูกปิด และล็อตที่มัน (อาจ) อ้างถึงยังอยู่สถานะเดิมทุกอย่าง — ไม่มีการเขียนบางส่วน';
    case 'list':
      return '';
  }
}

function isWriteContext(context: NcrFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case NCR_REFERENCE_NOT_FOUND:
      return {
        heading: 'ไม่พบเอกสารที่อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NCR_REFERENCE_NOT_FOUND} — ชนิดและ id ที่ระบุไม่ตรงกับแถวใดในตารางนั้นเลย ตรวจชนิดเอกสารและ id อีกครั้ง`,
      };
    case NCR_REFERENCE_PLANT_MISMATCH:
      return {
        heading: 'เอกสารที่อ้างถึงเป็นของอีกโรงงานหนึ่ง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NCR_REFERENCE_PLANT_MISMATCH} — เอกสารนี้มีอยู่จริงแต่ไม่ได้อยู่ในโรงงานที่ระบุไว้ในคำขอ`,
      };
    case NCR_CASE_NOT_OPEN:
      return {
        heading: 'เคสนี้ไม่ได้อยู่ที่สถานะเปิดใหม่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NCR_CASE_NOT_OPEN} — เฉพาะเคสที่ยังเป็น "เปิดใหม่" เท่านั้นที่ย้ายไป "กำลังสอบสวน" ได้ ดูสถานะปัจจุบันในรายละเอียดด้านล่าง`,
      };
    case NCR_CASE_IS_CLOSED:
      return {
        heading: 'เคสนี้ปิดไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NCR_CASE_IS_CLOSED} — เคสที่ปิดแล้วไม่มีวันถูกเปิดใหม่หรือแก้ไข ถ้าการตัดสินใจเดิมผิด ให้ยกเคสใหม่ (เปิดเคส) อ้างถึงเอกสารเดียวกันแทน สถานะปัจจุบันอยู่ในรายละเอียดด้านล่าง`,
      };
    case NCR_CASE_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — ฟิลด์ของเซิร์ฟเวอร์ถูกส่งไปด้วย',
        text: `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${NCR_CASE_FIELD_IS_SERVER_OWNED} — สถานะ การตัดสินใจ และผู้กระทำเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก — แจ้งผู้ดูแลระบบ`,
      };
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานที่ระบุอยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — เปิดเคส/สอบสวน/ปิดเคสเป็นการเขียนเข้าโรงงานหนึ่งเสมอ (D-004)`,
      };
    default:
      return null;
  }
}

function failureCopy(failure: NcrCaseFailure, context: NcrFailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return { heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้', text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว' };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${INVENTORY_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบเคสที่คำขอนี้อ้างถึง',
        text: `เซิร์ฟเวอร์ตอบ 404 — เคสนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สถานะปัจจุบันของเคสนี้ไม่รับสิ่งที่กด',
          text: `เซิร์ฟเวอร์ตอบ 409${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่ก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงรายการที่อ่านได้ไม่ครบ',
      };
    case 'cancelled':
      return { heading: 'ยกเลิกการเรียกข้อมูลแล้ว', text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ' };
  }
}

function detailEntries(failure: NcrCaseFailure): readonly (readonly [string, string])[] {
  if (!('error' in failure)) {
    return [];
  }
  return Object.entries(failure.error.details)
    .filter(([key]) => key !== 'rule')
    .map(([key, value]) => [key, formatDetailValue(value)] as const);
}

function formatDetailValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => formatDetailValue(entry)).join(', ');
  }
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

interface FailureNoticeProps {
  readonly failure: NcrCaseFailure;
  readonly context: NcrFailureContext;
  readonly testIdPrefix: string;
  readonly onRetry?: () => void;
}

function NcrFailureNotice({ failure, context, testIdPrefix, onRetry }: FailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const rule = ruleFromFailure(failure);
  const serverMessage = 'error' in failure ? failure.error.message : null;
  const entries = detailEntries(failure);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid={`${testIdPrefix}-failure`}
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code" data-testid={`${testIdPrefix}-rule`}>{rule}</code>
        </p>
      )}
      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid={`${testIdPrefix}-failure-message`}>
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}
      {entries.length === 0 ? null : (
        <dl className="cap-notice__details" data-testid={`${testIdPrefix}-failure-details`}>
          {entries.map(([key, value]) => (
            <div key={key} data-testid={`${testIdPrefix}-failure-detail-${key}`}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface NcrCaseScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly NcrCase[] }
  | NcrCaseFailure;

type ActiveModal =
  | { readonly kind: 'create' }
  | { readonly kind: 'investigate'; readonly ncrCase: NcrCase }
  | { readonly kind: 'close'; readonly ncrCase: NcrCase };

export function NcrCaseScreen({ permissions, apiBaseUrl, fetchImpl }: NcrCaseScreenProps) {
  const canCreate = can(permissions, INVENTORY_MODULE, 'CREATE');
  const canEdit = can(permissions, INVENTORY_MODULE, 'EDIT');
  const canApprove = can(permissions, INVENTORY_MODULE, 'APPROVE');
  const canWriteAnything = canCreate || canEdit || canApprove;

  const [items, setItems] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);

  const [plantCodeInput, setPlantCodeInput] = useState('');
  const [plantCodeFilter, setPlantCodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [modal, setModal] = useState<ActiveModal | null>(null);

  const [createFields, setCreateFields] = useState<CreateNcrCaseFields>(
    EMPTY_CREATE_NCR_CASE_FIELDS,
  );
  const [createFailure, setCreateFailure] = useState<NcrCaseFailure | null>(null);

  const [investigateNote, setInvestigateNote] = useState('');
  const [investigateFailure, setInvestigateFailure] = useState<NcrCaseFailure | null>(null);

  const [closeDisposition, setCloseDisposition] = useState<NcrDisposition | ''>('');
  const [closeNote, setCloseNote] = useState('');
  const [closeFailure, setCloseFailure] = useState<NcrCaseFailure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setItems((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchNcrCases(
      {
        ...(plantCodeFilter === '' ? {} : { plant_code: plantCodeFilter }),
        ...(statusFilter === '' ? {} : { status: statusFilter }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setItems(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, plantCodeFilter, statusFilter]);

  /* -------------------------------------------------------------- เปิดเคส -- */

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    setCreateFields(EMPTY_CREATE_NCR_CASE_FIELDS);
    setCreateFailure(null);
    setModal({ kind: 'create' });
  };

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const createBody = toCreateNcrCaseBody(createFields);

  const submitCreate = async () => {
    if (createBody === null || busy || !canCreate) {
      return;
    }
    setBusy(true);
    setCreateFailure(null);
    const result = await createNcrCase(createBody, { apiBaseUrl, fetchImpl });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCreateFailure(result);
      return;
    }
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* -------------------------------------------------------------- สอบสวน -- */

  const openInvestigate = (ncrCase: NcrCase) => {
    if (!canEdit) {
      return;
    }
    setInvestigateNote(ncrCase.investigation_note ?? '');
    setInvestigateFailure(null);
    setModal({ kind: 'investigate', ncrCase });
  };

  const investigateBody = toInvestigateNcrCaseBody(investigateNote);

  const submitInvestigate = async () => {
    if (modal?.kind !== 'investigate' || investigateBody === null || busy || !canEdit) {
      return;
    }
    setBusy(true);
    setInvestigateFailure(null);
    const result = await investigateNcrCase(modal.ncrCase.ncr_id, investigateBody, {
      apiBaseUrl,
      fetchImpl,
    });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่ปิดกล่อง** — นี่คือช่องทางที่แสดง 422 NCR_CASE_IS_CLOSED/NCR_CASE_NOT_OPEN
      // แทนที่จะซ่อนปุ่มไปเลย (invariant "A CLOSED CASE IS NEVER REOPENED")
      setInvestigateFailure(result);
      return;
    }
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  /* --------------------------------------------------------------- ปิดเคส -- */

  const openClose = (ncrCase: NcrCase) => {
    if (!canApprove) {
      return;
    }
    setCloseDisposition('');
    setCloseNote('');
    setCloseFailure(null);
    setModal({ kind: 'close', ncrCase });
  };

  const closeBody = toCloseNcrCaseBody(closeDisposition, closeNote);
  const consequencePreview =
    modal?.kind === 'close' ? closeConsequencePreview(modal.ncrCase, closeDisposition) : null;

  const submitClose = async () => {
    if (modal?.kind !== 'close' || closeBody === null || busy || !canApprove) {
      return;
    }
    setBusy(true);
    setCloseFailure(null);
    const result = await closeNcrCase(modal.ncrCase.ncr_id, closeBody, { apiBaseUrl, fetchImpl });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      // **ไม่ปิดกล่อง** ด้วยเหตุผลเดียวกับการสอบสวนข้างบน
      setCloseFailure(result);
      return;
    }
    setModal(null);
    setReloadToken((token) => token + 1);
  };

  const listItems = items.status === 'ready' ? items.items : [];

  return (
    <div className="cap-page" data-testid="ncr-cases">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            เคส NCR (Non-Conformance Report)
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text={
                <>
                  รายงานปัญหาที่พบในล็อต ใบรับเข้า หรือใบยืนยันงาน แล้วสอบสวนก่อนตัดสินใจ —{' '}
                  <strong>การปิดเคสด้วยการตัดสินใจคือการกระทำเดียวที่ย้ายสถานะคุณภาพของล็อต</strong>{' '}
                  (D-108) เมื่อเคสนั้นอ้างถึงล็อตโดยตรง
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="ncr-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังเปิด/สอบสวน/ปิดเคสไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>,{' '}
            <code className="cap-table__code">can_edit</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{INVENTORY_MODULE}</code> — รายการและตัวกรองยังใช้ได้
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------------------- ตัวกรอง -- */}

      <form
        className="cap-filters"
        data-testid="ncr-filter-form"
        onSubmit={(event) => {
          event.preventDefault();
          setPlantCodeFilter(toNcrPlantCodeFilter(plantCodeInput) ?? '');
        }}
      >
        <label className="cap-field">
          <span className="cap-field__label">รหัสโรงงาน</span>
          <input
            className="cap-input"
            type="text"
            data-testid="ncr-filter-plant"
            value={plantCodeInput}
            maxLength={PLANT_CODE_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setPlantCodeInput(event.target.value);
            }}
          />
        </label>

        <button type="submit" className="cap-button cap-button--ghost" data-testid="ncr-filter-apply">
          กรอง
        </button>

        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            data-testid="ncr-filter-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(toNcrStatusFilter(event.target.value) ?? '');
            }}
          >
            <option value="">ทุกสถานะ</option>
            {NCR_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ncrStatusLabel(status)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="ncr-create-open"
          disabled={!canCreate}
          title={canCreate ? undefined : `ต้องมีสิทธิ์ ${INVENTORY_MODULE} + CREATE จึงจะเปิดเคสใหม่ได้`}
          onClick={openCreate}
        >
          เปิดเคสใหม่
        </button>
        {canCreate ? null : (
          <span className="cap-field__hint" data-testid="ncr-create-open-reason">
            ต้องมีสิทธิ์ {INVENTORY_MODULE} + CREATE จึงจะเปิดเคสใหม่ได้
          </span>
        )}
      </form>

      {/* --------------------------------------------------------- เปิดเคส -- */}

      <Modal open={modal?.kind === 'create'} onClose={closeModal} title="เปิดเคส NCR ใหม่">
        <form
          className="cap-form"
          data-testid="ncr-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสโรงงาน</span>
            <input
              className="cap-input"
              data-testid="ncr-create-plant"
              value={createFields.plantCode}
              maxLength={PLANT_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, plantCode: event.target.value }));
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ชนิดเอกสารที่อ้างถึง</span>
            <select
              className="cap-input"
              data-testid="ncr-create-reference-type"
              value={createFields.referenceDocType}
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  referenceDocType: event.target.value,
                }));
              }}
            >
              <option value="">— ยังไม่ได้เลือก —</option>
              {NCR_REFERENCE_DOC_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ncrReferenceTypeLabel(type)}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับชนิดเอกสารที่อ้างถึง"
              text={
                <>
                  <strong>เฉพาะล็อตเท่านั้นที่ปิดเคสแล้วย้ายสถานะได้</strong> — ใบรับเข้าและใบยืนยัน
                  งานบันทึกการตัดสินใจไว้ แต่ไม่มีล็อตหนึ่งเดียวให้ย้าย (D-108)
                </>
              }
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">id ของเอกสารที่อ้างถึง</span>
            <input
              className="cap-input"
              data-testid="ncr-create-reference-id"
              value={createFields.referenceDocId}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  referenceDocId: event.target.value,
                }));
              }}
            />
            <InfoHint
              label="เกี่ยวกับ id ของเอกสารที่อ้างถึง"
              text="id ดิบของแถวในตารางที่ชนิดเอกสารด้านบนระบุ — id ที่ไม่มีจริงหรือเป็นของอีก
              โรงงานหนึ่งถูกปฏิเสธ 422 โดยไม่เขียนอะไรเลย"
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">สิ่งที่พบ</span>
            <textarea
              className="cap-input"
              data-testid="ncr-create-defect"
              value={createFields.defectDescription}
              maxLength={DEFECT_DESCRIPTION_MAX_LENGTH}
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  defectDescription: event.target.value,
                }));
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุการสอบสวนเบื้องต้น (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="ncr-create-investigation-note"
              value={createFields.investigationNote}
              maxLength={INVESTIGATION_NOTE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  investigationNote: event.target.value,
                }));
              }}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="ncr-create-submit"
              disabled={busy || createBody === null}
            >
              {busy ? 'กำลังเปิดเคส…' : 'เปิดเคส'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="ncr-create-cancel"
              disabled={busy}
              onClick={closeModal}
            >
              ยกเลิก
            </button>
          </div>

          {createFailure === null ? null : (
            <NcrFailureNotice failure={createFailure} context="create" testIdPrefix="ncr-create" />
          )}
        </form>
      </Modal>

      {/* ---------------------------------------------------------- สอบสวน -- */}

      <Modal
        open={modal?.kind === 'investigate'}
        onClose={closeModal}
        title={modal?.kind === 'investigate' ? `สอบสวนเคส — ${modal.ncrCase.ncr_id}` : 'สอบสวนเคส'}
      >
        {modal?.kind !== 'investigate' ? null : (
          <form
            className="cap-form"
            data-testid="ncr-investigate-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitInvestigate();
            }}
          >
            <p className="cap-field__hint" data-testid="ncr-investigate-current-status">
              สถานะปัจจุบัน:{' '}
              <StatusBadge tone={ncrStatusTone(modal.ncrCase.status)}>
                {ncrStatusLabel(modal.ncrCase.status)}
              </StatusBadge>
            </p>

            <label className="cap-field">
              <span className="cap-field__label">กำลังสอบสวนอะไร</span>
              <textarea
                className="cap-input"
                data-testid="ncr-investigate-note"
                value={investigateNote}
                maxLength={INVESTIGATION_NOTE_MAX_LENGTH}
                onChange={(event) => {
                  setInvestigateNote(event.target.value);
                }}
              />
              <InfoHint
                label="เกี่ยวกับช่องนี้"
                text={
                  <>
                    <strong>บังคับ</strong> — ย้ายเคสไปสอบสวนโดยไม่เขียนว่ากำลังสอบอะไรคือบันทึกที่
                    ไม่มีใครอ่านรู้เรื่อง
                  </>
                }
              />
            </label>

            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="ncr-investigate-submit"
                disabled={busy || investigateBody === null}
              >
                {busy ? 'กำลังบันทึก…' : 'ย้ายไปสอบสวน'}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="ncr-investigate-cancel"
                disabled={busy}
                onClick={closeModal}
              >
                ยกเลิก
              </button>
            </div>

            {investigateFailure === null ? null : (
              <NcrFailureNotice
                failure={investigateFailure}
                context="investigate"
                testIdPrefix="ncr-investigate"
              />
            )}
          </form>
        )}
      </Modal>

      {/* ------------------------------------------------------------ ปิดเคส -- */}

      <Modal
        open={modal?.kind === 'close'}
        onClose={closeModal}
        title={modal?.kind === 'close' ? `ปิดเคส — ${modal.ncrCase.ncr_id}` : 'ปิดเคส'}
      >
        {modal?.kind !== 'close' ? null : (
          <form
            className="cap-form"
            data-testid="ncr-close-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitClose();
            }}
          >
            <p className="cap-field__hint" data-testid="ncr-close-current-status">
              สถานะปัจจุบัน:{' '}
              <StatusBadge tone={ncrStatusTone(modal.ncrCase.status)}>
                {ncrStatusLabel(modal.ncrCase.status)}
              </StatusBadge>{' '}
              — อ้างถึง{' '}
              <strong>{ncrReferenceTypeLabel(modal.ncrCase.reference_doc_type)}</strong>{' '}
              <code className="cap-table__code" data-testid="ncr-close-reference-id">
                {modal.ncrCase.reference_doc_id}
              </code>
            </p>

            <fieldset className="cap-field" data-testid="ncr-close-dispositions">
              <legend className="cap-field__label">การตัดสินใจ — สี่แบบนี้ไม่เท่ากัน</legend>
              {DISPOSITION_DESCRIPTORS.map((descriptor) => (
                <label
                  key={descriptor.value}
                  className={
                    descriptor.destructive
                      ? 'cap-radio cap-radio--danger'
                      : 'cap-radio'
                  }
                  data-testid={`ncr-close-disposition-${descriptor.value}`}
                >
                  <input
                    type="radio"
                    name="ncr_close_disposition"
                    value={descriptor.value}
                    checked={closeDisposition === descriptor.value}
                    onChange={(event) => {
                      const { value } = event.target;
                      if (isNcrDisposition(value)) {
                        setCloseDisposition(value);
                      }
                    }}
                  />
                  <span>
                    <StatusBadge tone={descriptor.tone}>{descriptor.label}</StatusBadge>
                    {descriptor.destructive ? (
                      <strong> — ทำลายวัสดุถาวร</strong>
                    ) : null}
                    <br />
                    {descriptor.effect}
                  </span>
                </label>
              ))}
            </fieldset>

            {/**
             * **THE CONSEQUENCE HEADLINE** — แสดงทันทีที่เลือก disposition แล้ว
             * ก่อนที่ปุ่มยืนยันจะถูกกดเลย (acceptance ของ WO นี้: "removing that
             * preview must turn a test red")
             */}
            {consequencePreview === null ? null : (
              <div
                className="cap-notice"
                data-testid="ncr-close-consequence"
                role="status"
              >
                {consequencePreview.movesLot ? (
                  <p className="cap-notice__text">
                    การปิดเคสด้วยการตัดสินใจนี้จะเปลี่ยนสถานะคุณภาพของ{' '}
                    <strong>ล็อต</strong>{' '}
                    <code className="cap-table__code" data-testid="ncr-close-consequence-lot">
                      {consequencePreview.referenceDocId}
                    </code>{' '}
                    เป็น{' '}
                    <strong data-testid="ncr-close-consequence-status">
                      {lotStatusLabel(consequencePreview.targetStatus ?? '')}
                    </strong>
                  </p>
                ) : (
                  <p className="cap-notice__text" data-testid="ncr-close-consequence-no-lot">
                    เคสนี้อ้างถึง{' '}
                    <strong>{ncrReferenceTypeLabel(consequencePreview.referenceDocType)}</strong>{' '}
                    <code className="cap-table__code">{consequencePreview.referenceDocId}</code> —{' '}
                    <strong>จะไม่มีล็อตใดถูกเปลี่ยนสถานะเมื่อปิดเคสนี้</strong>{' '}
                    เพราะมีแต่เคสที่อ้างถึงล็อตโดยตรงเท่านั้นที่ย้ายสถานะ (D-108)
                  </p>
                )}
              </div>
            )}

            <label className="cap-field">
              <span className="cap-field__label">หมายเหตุประกอบการตัดสินใจ (ไม่บังคับ)</span>
              <input
                className="cap-input"
                data-testid="ncr-close-note"
                value={closeNote}
                maxLength={INVESTIGATION_NOTE_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setCloseNote(event.target.value);
                }}
              />
            </label>

            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="ncr-close-submit"
                disabled={busy || closeBody === null}
              >
                {busy ? 'กำลังปิดเคส…' : 'ยืนยันปิดเคส'}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="ncr-close-cancel"
                disabled={busy}
                onClick={closeModal}
              >
                ยกเลิก
              </button>
            </div>

            {closeFailure === null ? null : (
              <NcrFailureNotice failure={closeFailure} context="close" testIdPrefix="ncr-close" />
            )}
          </form>
        )}
      </Modal>

      {/* --------------------------------------------------------- รายการ --- */}

      {items.status === 'loading' ? (
        <div className="cap-notice" data-testid="ncr-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการเคส…</p>
        </div>
      ) : null}

      {items.status !== 'loading' && items.status !== 'ready' ? (
        <NcrFailureNotice
          failure={items}
          context="list"
          testIdPrefix="ncr-list"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {items.status === 'ready' && listItems.length === 0 ? (
        <div className="cap-notice" data-testid="ncr-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีเคส</StatusBadge>
          <h2 className="cap-notice__title">ไม่มีเคส NCR ตามตัวกรองนี้</h2>
        </div>
      ) : null}

      {items.status === 'ready' && listItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="เคส NCR ทั้งหมด">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="ncr-table">
              <caption className="cap-visually-hidden">
                เคส NCR พร้อมสถานะ อ้างอิง และการตัดสินใจ (ถ้าปิดแล้ว)
              </caption>
              <thead>
                <tr>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">อ้างอิง</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">สิ่งที่พบ</th>
                  <th scope="col">การตัดสินใจ</th>
                  <th scope="col">การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((ncrCase) => (
                  <tr key={ncrCase.ncr_id} data-testid={`ncr-row-${ncrCase.ncr_id}`}>
                    <td>
                      <code className="cap-table__code">{ncrCase.plant_code}</code>
                    </td>
                    <td>
                      {/* THE REFERENCE — ชนิดและ id ดิบ ไม่มีลิงก์ที่แต่งขึ้นเอง */}
                      {ncrReferenceTypeLabel(ncrCase.reference_doc_type)}{' '}
                      <code className="cap-table__code">{ncrCase.reference_doc_id}</code>
                    </td>
                    <td>
                      <StatusBadge tone={ncrStatusTone(ncrCase.status)}>
                        {ncrStatusLabel(ncrCase.status)}
                      </StatusBadge>
                    </td>
                    <td>{ncrCase.defect_description}</td>
                    <td>
                      {ncrCase.disposition === null ? (
                        '—'
                      ) : (
                        <StatusBadge
                          tone={
                            DISPOSITION_DESCRIPTORS.find(
                              (descriptor) => descriptor.value === ncrCase.disposition,
                            )?.tone ?? 'neutral'
                          }
                        >
                          {DISPOSITION_DESCRIPTORS.find(
                            (descriptor) => descriptor.value === ncrCase.disposition,
                          )?.label ?? ncrCase.disposition}
                        </StatusBadge>
                      )}
                    </td>
                    <td>
                      {/**
                       * **ปุ่มแสดงตามสิทธิ์เท่านั้น ไม่ตามสถานะของเคส** — เคสที่ปิด
                       * แล้วยังกดสอบสวน/ปิดซ้ำได้ เพื่อให้เห็นคำตอบ 422 จริงของ
                       * เซิร์ฟเวอร์แทนที่จะถูกซ่อนไปเงียบ ๆ (invariant ของ WO นี้)
                       */}
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`ncr-investigate-open-${ncrCase.ncr_id}`}
                        disabled={!canEdit}
                        title={
                          canEdit
                            ? undefined
                            : `ต้องมีสิทธิ์ ${INVENTORY_MODULE} + EDIT จึงจะสอบสวนได้`
                        }
                        onClick={() => {
                          openInvestigate(ncrCase);
                        }}
                      >
                        สอบสวน
                      </button>{' '}
                      <button
                        type="button"
                        className="cap-button cap-button--primary"
                        data-testid={`ncr-close-open-${ncrCase.ncr_id}`}
                        disabled={!canApprove}
                        title={
                          canApprove
                            ? undefined
                            : `ต้องมีสิทธิ์ ${INVENTORY_MODULE} + APPROVE จึงจะปิดเคสได้`
                        }
                        onClick={() => {
                          openClose(ncrCase);
                        }}
                      >
                        ปิดเคส
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
